import { Component, Inject, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { QuillViewComponent } from 'ngx-quill';
import { BehaviorSubject, combineLatest, interval, of, Subject, Subscription } from 'rxjs';
import { Issue, MailAttachement } from 'src/app/classes';
import { MatDialog } from '@angular/material/dialog';
import { SelectIncidentDialogComponent } from 'src/app/lib/issue/components/dialog/select-incident-dialog/select-incident-dialog.component';
import { MailService } from '../../../services/mail.service';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { EMailAudit } from '../../../classes';
import { listAnimation } from 'src/animations';
import { FileService } from 'src/app/lib/nextcloud/services/file.service';
import { ISSUE_PROVIDER_SERVICE, ISSUE_TRANSACTION_PROVIDER_SERVICE } from 'src/app/lib/issue/interfaces';
import { NavbarComponentService } from 'src/app/components/embedded/navbar/navbar-component.service';
import { executeIssueTransaction, IssueTransactionStatus, SpecialIssueTransactionRole } from 'src/app/lib/issue/globals';
import { IssueTransactionService } from 'src/app/lib/issue/services/issue-transaction.service';
import { IssueTransaction } from 'src/app/lib/issue/classes';
import { IssueTypes } from 'src/app/lib/shared/globals';
import { SelectIssueDialogComponent } from 'src/app/lib/issue/components/dialog/select-issue-dialog/select-issue-dialog.component';
import { plainMailToHTML } from 'src/app/globals';
import * as moment from 'moment';
import { RecoverSeenMailDialogComponent } from '../../dialog/recover-seen-mail-dialog/recover-seen-mail-dialog.component';
import { MailInboxComponentService } from './mail-inbox-component-service';

@Component({
  selector: 'app-mail-inbox',
  templateUrl: './mail-inbox.component.html',
  styleUrls: ['./mail-inbox.component.css'],
  providers: [
    MailInboxComponentService,
    { provide: ISSUE_PROVIDER_SERVICE, useClass: NavbarComponentService },
    { provide: ISSUE_TRANSACTION_PROVIDER_SERVICE, useExisting: ISSUE_PROVIDER_SERVICE }
  ],
  animations: [listAnimation]
})
export class MailInboxComponent implements OnDestroy, OnInit {

  preselectedMail: number;

  @ViewChild("quillComponent", { static: false }) quillComponent: QuillViewComponent;

  private _converting = new BehaviorSubject(false);
  converting$ = this._converting.asObservable();

  currentMailClicked = new Subject<Object>();

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(ISSUE_TRANSACTION_PROVIDER_SERVICE) private _service: NavbarComponentService,
    private _dialog: MatDialog,
    private _mailService: MailService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _fileService: FileService,
    private _viewContainerRef: ViewContainerRef,
    private _issueTransactionService: IssueTransactionService,
    public service: MailInboxComponentService
  ) { }

  answerMail() {
    this.service.currentMail$.subscribe((mail: EMailAudit) => {
      if (mail && mail.transmitter && typeof mail.transmitter.address === 'string') window.location.href = `mailto:${mail.transmitter.address}`;
    });
  }

  createIssue() {
    combineLatest([this.service.currentMail$, this.service.account$, this.service.account$.pipe(switchMap((account) => this._issueTransactionService.getIssueTransactionBySpecialRole(account === 'assignment' ? SpecialIssueTransactionRole.CREATE_ISSUE : SpecialIssueTransactionRole.CREATE_INCIDENT)))]).pipe(
      take(1),
      switchMap(([mail, account, transaction]) => executeIssueTransaction(this._issueTransactionService, transaction.guid, this._viewContainerRef, (transaction: IssueTransaction) => {
        var note = mail.htmlBody;
        if (!note) {
          if (typeof mail.body === 'string') note = plainMailToHTML(mail.body);
          else if (typeof mail.subject === 'string') note = `Erzeugt am ${moment().format('DD.MM.YYYY HH:mm')} Uhr aus E-Mail ${mail.subject}`;
        }
        this._service.provideEmptyIssue({
          'issuenote': note,
          'issuetitle': mail.subject,
          'issuestate': (account === 'assignment' ? 6 : transaction.result),
          'issuetype': (account === 'assignment' ? IssueTypes.Issue : IssueTypes.Incident)
        } as any);
        this._service.setIssueType(account === 'assignment' ? IssueTypes.Issue : IssueTypes.Incident);
      }))
    ).subscribe((result: any[]) => {
      if (result[0] === IssueTransactionStatus.Succeeded) this.finishIssueCreation(result[1]);
    });
  }

  eMailClicked = (eMail: EMailAudit) => {
    combineLatest([this.service.mailsLoading$, this.converting$]).subscribe(([loading, converting]) => {
      if (loading || converting) return;
      this.currentMailClicked.next();
      this._router.navigate([], {
        relativeTo: this._route,
        queryParamsHandling: 'merge',
        queryParams: { 'mail-id': eMail.messageId }
      });
    });
  }

  fileType = (type) => FileService.getFileType(type);

  finishIssueCreation(issue: Issue){
    this.service.currentMail$.pipe(take(1), switchMap(mail => this._mailService.linkMailToIssue(mail.guid, issue.issueid))).toPromise();
  }

  linkToIssue() {
    this.service.account$.pipe(switchMap((account) => {
      return (account === 'assignment' ? this._dialog.open(SelectIssueDialogComponent, {
        panelClass: "no-padding-dialog-container",
        autoFocus: false
      }) : this._dialog.open(SelectIncidentDialogComponent, {
        panelClass: "no-padding-dialog-container",
        autoFocus: false
      })).afterClosed();
    })).pipe(
      filter((issue) => issue ? true : false),
      switchMap((issue: Issue) => combineLatest([this.service.currentMail$, of(issue)])),
      switchMap(([mail, issue]) => this._mailService.linkMailToIssue(mail.guid, issue.issueid).pipe(map(() => issue)))
    ).subscribe((issue: Issue) => this._router.navigate(['/issue-details', issue.issueid]));
  }

  ngOnDestroy(): void {
    this.service.dispose();
    for (let subscription of this._subscriptions) subscription.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit() {
    this._subscriptions.push(...[
      this.service.currentMail$.pipe(filter(x => x ? true : false)).subscribe((eMail) => {
        if (eMail.htmlBody) {
          this._converting.next(true);
          this._mailService.resolveEMailCids(eMail, this.currentMailClicked.asObservable()).subscribe(() => {
            this.service.convertedBody = eMail.htmlBody;
            this._converting.next(false);
          });
        } else {
          this.service.convertedBody = typeof eMail.body === 'string' ? eMail.body.replace(/(?:\r\n|\r|\n)/g, '<br>') : eMail.body;
          this._converting.next(false);
        }
      }),
      this.service.account$.pipe(distinctUntilChanged()).subscribe((account) => this.service.refresh(account)),
      combineLatest([this.service.account$, interval(60000)]).subscribe(([account, _]) => this.service.refresh(account))
    ]);
  }

  openFile(attachement: MailAttachement) {
    this.service.currentMail$.pipe(take(1), switchMap((mail: EMailAudit) => this._mailService.downloadMailAttachement(mail.messageId, mail.inbox, attachement.name)))
      .subscribe((blob: Blob) => this._fileService.previewBlob(blob, attachement.type, attachement.name));
  }

  recoverSeenMail() {
    this.service.account$.pipe(take(1)).subscribe((account) => {
      this._dialog.open(RecoverSeenMailDialogComponent, {
        panelClass: 'no-padding-dialog-container',
        data: account,
        viewContainerRef: this._viewContainerRef
      });
    });
  }

  removeMail() {
    combineLatest([this.service.currentMail$, this.service.account$]).pipe(take(1))
      .subscribe(([mail, account]) => this._mailService[account === 'incident' ? 'markIncidentMailAsSeen' : 'markAssignmentMailAsSeen'](mail.messageId).subscribe(() => {
        this.service.clearCurrentMail();
        this.service.refresh(account);
      }));
  }

}
